import { isEqual } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { Button, Stack, ListItemText, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getIssuedCheques, getIssuedChequeTotal } from 'src/redux/slices/issued-cheque';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IIssuedChequeSort,
  IIssuedChequeFilters,
  IIssuedChequeListItem,
  IssuedChequeBank,
  IssuedChequeType,
  ISSUED_CHEQUE_STATUS_OPTIONS,
} from 'src/types/issued-cheque';

import IssuedChequeTableRow from '../issued-cheque-table-row';
import IssuedChequeTableToolbar from '../issued-cheque-table-toolbar';
import IssuedChequeCalendar, { CalendarView, CalendarRange } from '../issued-cheque-calendar';

const STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...ISSUED_CHEQUE_STATUS_OPTIONS];

const TABLE_HEAD = [
  { id: 'chequeNumber', label: 'Cheque Number', align: 'left', primary: true },
  { id: 'payee', label: 'Payee', align: 'left', primary: true },
  { id: 'chequeDate', label: 'Cheque Date', align: 'left', primary: true },
  { id: 'amountInCents', label: 'Amount', align: 'right', primary: true },
  { id: 'type', label: 'Type', align: 'left', primary: true },
  { id: 'status', label: 'Status', align: 'left', primary: true },
  { id: '', primary: true },
];

const SORTABLE_COLUMNS = ['chequeNumber', 'payee', 'chequeDate', 'amountInCents', 'status'];

const defaultFilters: IIssuedChequeFilters = { generalSearch: '', type: null, bank: null };

const defaultSort: IIssuedChequeSort = { sortBy: 'chequeDate', order: 'asc' };

const DEFAULT_ROWS_PER_PAGE = 25;

type ViewMode = 'calendar' | 'list';

// The screen's state (view, tab, page, filters, sort, calendar position) lives
// in the URL, so coming back from a cheque's details lands on the same screen.
const readPositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const isCalendarView = (value: string | null): value is CalendarView =>
  value === 'dayGridMonth' || value === 'dayGridWeek';

const isIsoDate = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);

export default function IssuedChequeListView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatcher();
  const settings = useSettingsContext();
  const { user: authUser } = useAuthContext();

  const { status, cheques, error, total } = useAppSelector((state) => state.issuedCheque);

  const table = useTable({
    defaultRowsPerPage: readPositiveInt(searchParams.get('rowsPerPage'), DEFAULT_ROWS_PER_PAGE),
    defaultCurrentPage: readPositiveInt(searchParams.get('page'), 1) - 1,
    defaultDense: true,
  });

  const { page, rowsPerPage } = table;
  const denseHeight = table.dense ? 60 : 80;

  const [viewMode, setViewMode] = useState<ViewMode>(
    searchParams.get('view') === 'list' ? 'list' : 'calendar'
  );

  const [statusTab, setStatusTab] = useState<string>(() => {
    const tab = searchParams.get('tab');
    return STATUS_OPTIONS.some((option) => option.value === tab) ? (tab as string) : 'all';
  });

  const [filters, setFilters] = useState<IIssuedChequeFilters>(() => {
    const type = searchParams.get('type');
    const bank = searchParams.get('bank');

    return {
      generalSearch: searchParams.get('search') ?? '',
      type: Object.values(IssuedChequeType).includes(type as IssuedChequeType)
        ? (type as IssuedChequeType)
        : null,
      bank: Object.values(IssuedChequeBank).includes(bank as IssuedChequeBank)
        ? (bank as IssuedChequeBank)
        : null,
    };
  });

  const [sort, setSort] = useState<IIssuedChequeSort>(() => {
    const sortBy = searchParams.get('sortBy');
    const order = searchParams.get('order');

    return {
      sortBy: sortBy && SORTABLE_COLUMNS.includes(sortBy) ? sortBy : defaultSort.sortBy,
      order: order === 'asc' || order === 'desc' ? order : defaultSort.order,
    };
  });

  // Where the calendar is; kept here so switching to the list and back, or
  // coming back from a cheque, reopens the same month / week.
  const [calendar, setCalendar] = useState<{ view: CalendarView; date?: string }>(() => {
    const view = searchParams.get('calView');
    const date = searchParams.get('date');

    return {
      view: isCalendarView(view) ? view : 'dayGridMonth',
      date: isIsoDate(date) ? date : undefined,
    };
  });

  const [range, setRange] = useState<{ start: string; end: string } | null>(null);

  const canReset = !isEqual(defaultFilters, filters);

  const queryFilters = useMemo(
    () => ({
      generalSearch: filters.generalSearch,
      status: statusTab === 'all' ? null : statusTab,
      type: filters.type,
      bank: filters.bank,
    }),
    [filters, statusTab]
  );

  const handleFilters = useCallback(
    (name: keyof IIssuedChequeFilters, value: string | null) => {
      table.onResetPage();
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [table]
  );

  const handleRangeChange = useCallback((next: CalendarRange) => {
    setCalendar({ view: next.view, date: next.date });
    setRange({ start: next.start, end: next.end });
  }, []);

  const handleFilterStatus = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      setStatusTab(newValue);
      table.onResetPage();
    },
    [table]
  );

  useEffect(() => {
    const next = new URLSearchParams();

    if (viewMode === 'list') next.set('view', 'list');
    if (statusTab !== 'all') next.set('tab', statusTab);
    if (page > 0) next.set('page', String(page + 1));
    if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) next.set('rowsPerPage', String(rowsPerPage));
    if (filters.generalSearch) next.set('search', filters.generalSearch);
    if (filters.type) next.set('type', filters.type);
    if (filters.bank) next.set('bank', filters.bank);
    if (sort.sortBy !== defaultSort.sortBy) next.set('sortBy', sort.sortBy);
    if (sort.order !== defaultSort.order) next.set('order', sort.order);
    if (calendar.view !== 'dayGridMonth') next.set('calView', calendar.view);
    if (calendar.date) next.set('date', calendar.date);

    // Replace, so paging around doesn't pile up history entries.
    setSearchParams(next, { replace: true });
  }, [viewMode, statusTab, page, rowsPerPage, filters, sort, calendar, setSearchParams]);

  useEffect(() => {
    if (viewMode !== 'list') return;

    dispatch(
      getIssuedCheques({
        ...queryFilters,
        page,
        limit: rowsPerPage,
        sortBy: sort.sortBy,
        sort: sort.order,
      })
    );
  }, [dispatch, viewMode, queryFilters, page, rowsPerPage, sort]);

  // The total follows what is on screen: the visible month / week on the
  // calendar, or everything matching the tab and filters in the list.
  useEffect(() => {
    if (viewMode === 'calendar') {
      if (!range) return;
      dispatch(
        getIssuedChequeTotal({ ...queryFilters, startDate: range.start, endDate: range.end })
      );
    } else {
      dispatch(getIssuedChequeTotal(queryFilters));
    }
  }, [dispatch, viewMode, queryFilters, range]);

  const canCreate = hasPermission(
    authUser,
    'any',
    AuthPermissions.CREATE_ISSUED_CHEQUE,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Issued Cheques"
        links={[]}
        action={
          <Stack direction="row" alignItems="center" gap={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={(_, value: ViewMode | null) => value && setViewMode(value)}
            >
              <ToggleButton value="calendar" aria-label="Calendar view">
                <Iconify icon="solar:calendar-date-bold" width={20} sx={{ mr: 0.5 }} />
                Calendar
              </ToggleButton>

              <ToggleButton value="list" aria-label="List view">
                <Iconify icon="solar:list-bold" width={20} sx={{ mr: 0.5 }} />
                List
              </ToggleButton>
            </ToggleButtonGroup>

            <ListItemText
              primary={viewMode === 'calendar' ? 'Total Value (shown)' : 'Total Value'}
              secondary={fCurrencyRupees(total.data.totalAmount)}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
            />

            {canCreate && (
              <Button
                component={RouterLink}
                href={paths.dashboard.issuedCheque.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Cheque
              </Button>
            )}
          </Stack>
        }
        sx={{ mb: { xs: 3 } }}
      />

      <Card>
        <Tabs
          value={statusTab}
          onChange={handleFilterStatus}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {STATUS_OPTIONS.map((tab) => (
            <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <IssuedChequeTableToolbar
          filters={filters}
          onFilters={handleFilters}
          canReset={canReset}
          onReset={() => {
            table.onResetPage();
            setFilters(defaultFilters);
          }}
        />

        {viewMode === 'calendar' ? (
          <IssuedChequeCalendar
            filters={queryFilters}
            initialView={calendar.view}
            initialDate={calendar.date}
            onRangeChange={handleRangeChange}
          />
        ) : (
          <>
            <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
              <Scrollbar>
                <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                  <TableHeadCustom
                    order={sort.order}
                    orderBy={sort.sortBy}
                    headLabel={TABLE_HEAD}
                    rowCount={cheques.items.length}
                    onSort={(id) => {
                      if (!SORTABLE_COLUMNS.includes(id)) return;

                      if (id === sort.sortBy) {
                        setSort((prev) => ({
                          ...prev,
                          order: prev.order === 'asc' ? 'desc' : 'asc',
                        }));
                      } else {
                        setSort({ sortBy: id, order: 'asc' });
                      }
                    }}
                  />

                  <TableBody>
                    {status === 'loading'
                      ? [...Array(table.rowsPerPage)].map((_, index) => (
                          <TableSkeleton key={index} sx={{ height: denseHeight }} />
                        ))
                      : cheques.items.map((row: IIssuedChequeListItem) => (
                          <IssuedChequeTableRow key={row._id} row={row} />
                        ))}

                    {status !== 'failure' && cheques.count === 0 && (
                      <TableNoData message="No cheques found" notFound={cheques.count === 0} />
                    )}

                    {status === 'failure' && <TableErrorData notFound error={error} />}
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              dense
              count={cheques.count}
              page={table.page}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
              onChangeDense={table.onChangeDense}
            />
          </>
        )}
      </Card>
    </Container>
  );
}
